import { normalizeUsername, USERNAME_HELP, type ProfileUpdate, type UserProfile } from './profile';
import { escapeSignInText } from './sign-in-form';

export function renderUsernameForm(profile: UserProfile, setup: boolean): string {
  return `
    <h2 id="username-title">${setup ? 'Choose your username' : 'Edit username'}</h2>
    <p class="card-copy">Other players will see this name in workouts and notifications.</p>
    ${setup ? `<p class="card-copy">Your generated username is <strong>${escapeSignInText(profile.username)}</strong>.</p>` : ''}
    <form id="username-form" class="username-form" novalidate>
      <fieldset>
        <label for="username-input">Username</label>
        <input id="username-input" name="username" type="text" autocomplete="nickname" autocapitalize="none"
          spellcheck="false" maxlength="24" value="${escapeSignInText(setup ? '' : profile.username)}"
          aria-describedby="username-help username-error" />
        <p id="username-help" class="auth-note">${USERNAME_HELP} Usernames must be unique.</p>
        <p id="username-error" class="username-error" role="alert"></p>
        <div class="username-actions">
          <button id="username-save" class="btn" type="submit">${setup ? 'Save username' : 'Save'}</button>
          <button id="username-secondary" class="btn btn-secondary" type="button">${setup ? 'Keep generated username' : 'Cancel'}</button>
        </div>
      </fieldset>
      <p id="username-status" class="auth-note" role="status"></p>
    </form>`;
}

export function mountUsernameForm(
  element: HTMLElement,
  setup: boolean,
  save: (update: ProfileUpdate) => Promise<void>,
  cancel: () => void,
): void {
  const form = element.querySelector<HTMLFormElement>('#username-form')!;
  const input = element.querySelector<HTMLInputElement>('#username-input')!;
  const fieldset = form.querySelector('fieldset')!;
  const error = element.querySelector<HTMLElement>('#username-error')!;
  const status = element.querySelector<HTMLElement>('#username-status')!;
  let saving = false;
  const submit = async (keepGenerated: boolean) => {
    if (saving) return;
    error.textContent = '';
    input.removeAttribute('aria-invalid');
    try {
      const update: ProfileUpdate = keepGenerated ? { completeUsernameSetup: true } : {
        username: normalizeUsername(input.value),
        ...(setup ? { completeUsernameSetup: true as const } : {}),
      };
      saving = true;
      fieldset.disabled = true;
      form.setAttribute('aria-busy', 'true');
      status.textContent = 'Saving…';
      await save(update);
    } catch (reason) {
      error.textContent = reason instanceof Error ? reason.message : 'Could not save your username. Try again.';
      input.setAttribute('aria-invalid', 'true');
    } finally {
      saving = false;
      fieldset.disabled = false;
      form.setAttribute('aria-busy', 'false');
      status.textContent = '';
      if (error.textContent && element.isConnected) input.focus();
    }
  };
  form.addEventListener('submit', (event) => { event.preventDefault(); void submit(false); });
  element.querySelector('#username-secondary')!.addEventListener('click', () => {
    if (setup) void submit(true);
    else if (!saving) cancel();
  });
  if (element instanceof HTMLDialogElement) {
    element.addEventListener('cancel', (event) => {
      event.preventDefault();
      if (!saving) cancel();
    });
  }
  input.focus();
}
